import type { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { formatShortDate } from "@app/shared";

import { clockLength } from "../../lib/time";

import type { CapturedLecture } from "../../lib/data";
import { LectureStatusChip } from "../school/Chrome";
import { DownloadTranscriptButton } from "../school/DownloadTranscript";
import { hueVar } from "../school/derive";

// One recording, as a row. Used on /record's "Recent recordings" list.
//
// WHY IT SAYS "NO COURSE" IN WORDS rather than leaving the line blank: an unfiled recording is
// the one thing on this list that needs an action, and a blank space is indistinguishable from a
// course code that failed to load. The status chip is the SAME component the School screens use
// — one vocabulary for one enum, rather than a second set of labels for the same five states.

export function RecordingRow({ lecture }: { lecture: CapturedLecture }) {
  // THE ROW IS A LINK, AND A LINK MAY NOT CONTAIN A BUTTON. So the row is a flex box whose link
  // takes the whole width, with the download control as a SIBLING at the right edge rather than a
  // child — valid HTML, and the pointer never has to guess which of two nested targets it hit.
  // The control appears only when there are words to download (`hasTranscript`, the pipeline's
  // own flag — never inferred from the status chip).
  return (
    <div
      style={{ "--mo-rule": hueVar(lecture.courseCode ?? "") } as CSSProperties}
      className="mo-rule flex min-h-[56px] items-center gap-2 rounded-row border border-line bg-surface pl-3 pr-2 py-[10px] shadow-card"
    >
      <Link
        to={`/recordings/${lecture.id}`}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-[1.25]">
            {lecture.title ?? "Untitled recording"}
          </p>
          <p className="mt-[4px] truncate text-[11.5px] leading-none text-muted">
            {formatShortDate(lecture.date)}
            {" · "}
            {lecture.courseCode ?? "No course"}
            {/* ABSENT WHEN UNKNOWN. Nothing in the pipeline derives a length from a byte count, so a
              recording whose container never reported one simply does not state it. */}
            {lecture.durationSec !== null &&
              ` · ${clockLength(lecture.durationSec)}`}
          </p>
        </div>
        <LectureStatusChip status={lecture.status} />
      </Link>
      {lecture.hasTranscript && (
        <DownloadTranscriptButton lectureId={lecture.id} variant="icon" />
      )}
    </div>
  );
}
